import axios from 'axios';
import dayjs from 'dayjs';
import * as querystring from 'querystring';
import Logger, { LoggingModule } from 'services/Logger';
import { apiKeySelector } from 'store/appSettings/selectors';
import { authLiteDataSelector } from 'store/authentication/selectors';
import simpleOauth2, { Token } from 'simple-oauth2-browser';
import { OAuthParams } from 'store/authentication/configs.web';
import { AuthResult } from 'store/authentication/reducers';
import { RootState } from 'store/types';
import jsonwebtoken from 'jsonwebtoken';
import { AuthLiteResponse, RefreshResult } from 'store/authentication/types';
import { DFDHeaders, DFDRequestHeaders, JWTTokenPayload } from '../serverTypes/authentication';

interface PingOauthRefreshResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export function getForgotUserUrl(params: OAuthParams) {
  const { authorizeHost, forgotUserPath, landingUrl } = params;

  return landingUrl
    ? `${authorizeHost}${forgotUserPath}?AdapterId=ConsumerUsernameForm&TargetResource=${encodeURIComponent(
        landingUrl
      )}`
    : `${authorizeHost}${forgotUserPath}?AdapterId=ConsumerUsernameForm`;
}

async function createOauth2({
  clientId,
  clientSecret = '',
  tokenHost,
  tokenPath,
  authorizeHost,
  authorizePath
}: OAuthParams) {
  return simpleOauth2.create({
    client: {
      id: clientId,
      secret: clientSecret
    },
    auth: {
      tokenHost,
      tokenPath,
      authorizeHost,
      authorizePath
    },
    options: {}
  });
}

export async function getAuthUrl(params: OAuthParams) {
  const {
    tokenHost,
    tokenPath,
    authorizeHost,
    authorizePath,
    redirectUrl,
    requestedScopes,
    clientId,
    audUrl = ''
  } = params;

  const oauth2 = await createOauth2({
    clientId,
    tokenHost,
    tokenPath,
    authorizeHost,
    authorizePath
  });

  const scope = typeof requestedScopes === 'string' ? requestedScopes : requestedScopes.join(' ');
  const authParams = {
    redirect_uri: redirectUrl,
    scope
  };

  if (audUrl) {
    authParams.aud = audUrl;
  }

  const authorizationUri = oauth2.authorizationCode.authorizeURL(authParams);
  return authorizationUri;
}

export async function getAuthToken(params: Token, code: string) {
  const { tokenHost, tokenPath, redirectUrl, clientId, clientSecret = '' } = params;
  try {
    const result = await axios.post(
      tokenHost + tokenPath,
      querystring.stringify({
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUrl,
        client_id: clientId,
        client_secret: clientSecret
      }),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }
    );

    return result.data;
  } catch (err) {
    throw err;
  }
}
interface OAuth2Response {
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token: string;
  token_type: string;
  scopes: string;
}

export function formatAuthForStorage(payload: OAuth2Response) {
  return {
    accessToken: payload.access_token,
    accessTokenExpirationDate: dayjs().add(payload.expires_in, 'second'),
    authorizeAdditionalParameters: {},
    tokenAdditionalParameters: {},
    additionalParameters: {},
    idToken: payload.id_token,
    refreshToken: payload.refresh_token,
    tokenType: payload.token_type,
    scopes: payload.scopes
  };
}

export default class AuthServices {
  config: OAuthParams;

  constructor(config: OAuthParams) {
    this.config = config;
  }

  /**
   * has been authenticated and not expired
   */
  static isAuthenticated = (auth: AuthResult): boolean => {
    if (!auth) {
      return false;
    }

    const expiryDate = auth.accessTokenExpirationDate;

    return dayjs(expiryDate).isAfter(dayjs().add(1, 'minute'));
  };

  /**
   * Shall token to be refreshed
   * returns true: if access token expires in expireMinutes minutes, refresh token
   */
  static shallRefreshToken = (auth: AuthResult, expireMinutes: number) => {
    if (!auth) {
      return false;
    }

    return dayjs()
      .add(expireMinutes, 'minute')
      .isAfter(dayjs(auth.accessTokenExpirationDate));
  };

  /**
   * Authenticate User
   * If Prompt is required, pass usePrompt as true
   */
  async authenticateUser(): Promise<any> {
    window.location.href = await getAuthUrl(this.config);
  }

  /**
   * Method: forgotUsername
   * Description: sets the location to the forgot username page in ping
   */
  forgotUsername() {
    window.location.href = getForgotUserUrl(this.config);
  }

  /**
   *  Refresh Token
   *  use normal function instead of arrow function to enable mock
   */
  async refreshToken(token: string): Promise<Partial<RefreshResult>> {
    const { tokenHost, tokenPath, clientId, clientSecret = '' } = this.config;

    const payload: Record<string, any> = {
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: token
    };

    if (!!clientSecret) {
      payload.client_secret = clientSecret;
    }

    const result = await axios.post<PingOauthRefreshResponse>(
      tokenHost + tokenPath,
      querystring.stringify(payload),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }
    );

    return {
      accessToken: result.data.access_token,
      accessTokenExpirationDate: dayjs()
        .add(result.data.expires_in, 'second')
        .toString(),
      tokenType: result.data.token_type
    };
  }

  /**
   * Revoke Token through axios directly
   */
  revokeRefreshToken = async (token: string): Promise<void> => {
    const { tokenHost, revokePath } = this.config;
    try {
      const revocationEndpoint = tokenHost + revokePath;

      if (token && revocationEndpoint) {
        const response = axios.post(
          revocationEndpoint,
          querystring.stringify({
            token,
            // eslint-disable-next-line
            token_type_hint: 'refresh_token'
          }),
          {
            auth: {
              username: this.config.clientId as string,
              password: this.config.clientSecret as string
            },
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            }
          }
        );

        Logger.debug(`${LoggingModule.authService}SUCCESS AuthServices:revokeToken()`, response);
      }
    } catch (error) {
      Logger.error(`${LoggingModule.authService}FAIL AuthServices:revokeToken()`, error);
      throw error;
    }
  };
}

/**
 * Method: getAuthLiteHeaders
 * Description: gets authLite Headers from store
 *  - apikey
 *  - authorization: Bearer
 *  - x-dfd-consumer-id
 *  - x-patient-id
 *  - x-dfd-event-id
 *  - x-clinical-event-id
 */
export function getAuthLiteHeaders(state: RootState, docId?: string): DFDRequestHeaders {
  const authLiteData: AuthLiteResponse = authLiteDataSelector(state) as AuthLiteResponse;
  const jwt: { payload: JWTTokenPayload } = jsonwebtoken.decode(authLiteData.accessToken, {
    complete: true
  });
  const headers: DFDRequestHeaders = {
    [DFDHeaders.kongApiKey]: apiKeySelector(state),
    [DFDHeaders.authorization]: `Bearer ${authLiteData.accessToken}`,
    [DFDHeaders.consumerId]: jwt?.payload?.sub,
    [DFDHeaders.patientId]: authLiteData.personId,
    [DFDHeaders.dfdEventId]: authLiteData.dfdEventId,
    [DFDHeaders.eventIds]:
      (authLiteData.mhpDetails?.headers?.[DFDHeaders.eventIds] as string) ||
      authLiteData.clinicalEventId,
    [DFDHeaders.clinicalEventId]: authLiteData.clinicalEventId,
    [DFDHeaders.mhpAdapter]:
      (authLiteData.mhpDetails?.headers?.[DFDHeaders.mhpAdapter] as string) || undefined,
    [DFDHeaders.filterSource]:
      (authLiteData.mhpDetails?.headers?.[DFDHeaders.filterSource] as string) || undefined
  };
  if (docId) {
    headers[DFDHeaders.contentId] = docId;
  }
  return headers;
}
